import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCode, FaDesktop, FaMobileAlt, FaServer, FaDatabase, FaRocket } from 'react-icons/fa';
import { Link } from 'react-router-dom';

// Components
import PageHeader from '../components/ui/PageHeader';
import ServiceCard from '../components/services/ServiceCard';
import PageTransition from '../components/ui/PageTransition';

const Services = () => {
  const services = [
    {
      id: 1,
      icon: <FaCode aria-hidden />,
      title: 'Web Development',
      description: 'Custom web applications built with modern frameworks like React, Vue, and Angular. Responsive, accessible, and optimized for performance.',
      features: [
        'Responsive design for all devices',
        'Interactive UI/UX',
        'Performance optimization',
        'SEO-friendly structure'
      ]
    },
    {
      id: 2,
      icon: <FaDesktop aria-hidden />,
      title: 'Frontend Development',
      description: 'Creating beautiful, intuitive user interfaces with modern JavaScript frameworks and CSS preprocessors.',
      features: [
        'Component-based architecture',
        'State management solutions',
        'Animation and transitions',
        'Cross-browser compatibility'
      ]
    },
    {
      id: 3,
      icon: <FaServer aria-hidden />,
      title: 'Backend Development',
      description: 'Robust server-side applications using Node.js, Express, and other modern technologies.',
      features: [
        'RESTful API development',
        'Authentication & authorization',
        'Server-side rendering',
        'Microservices architecture'
      ]
    },
    {
      id: 4,
      icon: <FaMobileAlt aria-hidden />,
      title: 'Responsive Web Design',
      description: 'Creating websites that work seamlessly across all devices and screen sizes.',
      features: [
        'Mobile-first approach',
        'Fluid layouts',
        'Touch-friendly interfaces',
        'Optimized media loading'
      ]
    },
    {
      id: 5,
      icon: <FaDatabase aria-hidden />,
      title: 'Database Design',
      description: 'Efficient database architecture for optimal performance and scalability.',
      features: [
        'Schema design',
        'Query optimization',
        'Data migration',
        'NoSQL & SQL solutions'
      ]
    },
    {
      id: 6,
      icon: <FaMobileAlt aria-hidden />,
      title: 'App Development',
      description: 'End‑to‑end mobile app development for iOS and Android using modern cross‑platform stacks.',
      features: [
        'Cross‑platform (React Native / Flutter)',
        'Authentication, push notifications, deep links',
        'Offline support and secure storage',
        'App Store & Play Store deployment'
      ]
    }
  ];

  return (
    <PageTransition>
      <ServicesContainer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
      >
        <PageHeader
          title="My Services"
          subtitle="Specialized solutions to help your business grow"
        />

        <ServicesGrid>
          {services.map(service => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </ServicesGrid>

        <ContactSection>
          <h3>Need a custom solution?</h3>
          <p>Let's discuss how I can help with your specific requirements.</p>
          <ContactButton to="/contact">Get in Touch</ContactButton>
        </ContactSection>
      </ServicesContainer>
    </PageTransition>
  );
};

const ServicesContainer = styled(motion.div)`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactSection = styled.div`
  margin-top: 4rem;
  text-align: center;
  padding: 3rem;
  background-color: var(--bg-secondary);
  border-radius: 8px;
  
  h3 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    color: var(--text-color);
  }
  
  p {
    font-size: 1.1rem;
    margin-bottom: 2rem;
    color: var(--text-secondary);
  }
`;

const ContactButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background-color: var(--primary-color);
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

export default Services;